import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import socket from '../socket';
import nodeApi from '../nodeApi';
export interface Notification {
  id: number;
  message: string;
  createdAt: string;
  isRead: boolean;
  leaveId: number;
  interviewId: number;
  userId: number;
  type: string,
  meetingLink?: string; 
}
interface NotificationContextType {
  notifications: Notification[];
  fetchUnreadNotifications: () => void;
  markAllAsRead: () => void;
  fetchTodaysUnreadNotifications: () => void;
  loading?: boolean;
  markAsRead: (notificationId: number) => void;
}
const NotificationContext = createContext<NotificationContextType | undefined>(undefined);
export const useNotificationContext = () => {
  const context = useContext(NotificationContext);
  if (!context) throw new Error('useNotificationContext must be used within NotificationProvider');
  return context;
};
interface Props {
  children: React.ReactNode;
}
export const NotificationProvider: React.FC<Props> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [userId, setUserId] = useState<number | null>(() => {
    const stored = localStorage.getItem("user");
    try {
      return stored ? JSON.parse(stored).id : null;
    } catch {
      return null;
    }
  });
  const isFetchingRef = useRef(false);
  // Update userId whenever localStorage 'user' changes
  useEffect(() => {
    const interval = setInterval(() => {
      const stored = localStorage.getItem("user");
      try {
        const newId = stored ? JSON.parse(stored).id : null;
        setUserId(prev => (prev !== newId ? newId : prev));
      } catch {
        setUserId(null);
      }
    }, 500); // adjust this polling interval if needed
    return () => clearInterval(interval);
  }, []);

  const fetchUnreadNotifications = useCallback(async () => {
    if (isFetchingRef.current || !userId) return;
    isFetchingRef.current = true;
    setLoading(true);
    try {
      const data = await nodeApi<Notification[]>(`/unread/${userId}`);
      setNotifications(data);
    } catch (err) {
      console.error('Failed to fetch notifications', err);
    } finally {
      isFetchingRef.current = false;
      setLoading(false);
    }
  }, [userId]);
  const markAllAsRead = async () => {
    setLoading(true);
    try {
      await nodeApi<any>(`/mark-all-read/${userId}`, {
        method: "PATCH",
      });
      fetchUnreadNotifications();
    } catch (err) {
      console.error('Failed to mark all as read', err);
    } finally {
      setLoading(false);
    }
  };
  const markAsRead = async (notificationId: number) => {
    setLoading(true);
    try {
      await nodeApi<any>(`/mark-as-read/${notificationId}`, {
        method: "PATCH",
      });
      fetchUnreadNotifications();
    } catch (err) {
      console.error('Failed to mark as read', err);
    } finally {
      setLoading(false);
    }
  };
  const fetchTodaysUnreadNotifications = async () => {
    setLoading(true);
    try {
      const data = await nodeApi<Notification[]>(`/unread-today/${userId}`);
      setNotifications(data);
    } catch (err) {
      console.error("Failed to fetch today's unread notifications", err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
  if ("Notification" in window && Notification.permission !== "granted") {
    Notification.requestPermission();
  }
}, []);

useEffect(() => {
  if (!userId) return;

  socket.connect();

  const onConnect = () => {
    console.log("Socket connected:", userId);
    socket.emit("join", userId);
    fetchUnreadNotifications();
  };

  const handleNotification = (notification: Notification & { meetingLink?: string }) => {
    console.log("SOCKET RECEIVED:", notification);

    //  SAFETY: ensure valid date
    const date = new Date(notification.createdAt);
    const safeCreatedAt = isNaN(date.getTime())
      ? new Date().toISOString()
      : date.toISOString();

    const sanitized: Notification = {
      ...notification,
      createdAt: safeCreatedAt,
      meetingLink: notification.meetingLink || undefined
    };

    // Browser Notification
    if ("Notification" in window && Notification.permission === "granted") {
  let title = "Log Binary";

  const type = sanitized.type?.toLowerCase() || "";

  if (type.includes("birthday")) {
    title = "Happy Birthday!";
  } 
  else if (type.includes("anniversary")) {
    title = "Work Anniversary";
  } 
  else if (type.includes("holiday")) {
    title = "Holiday Announcement";
  } 
  else if (type.includes("leave")) {
    title = "Leave Update";
  } 
  else if (type.includes("interview")) {
    title = "Interview Assigned";
  }

  const browserNotification = new Notification(title, {
    body: sanitized.message,
    icon: "/logo192.png"
  });

  browserNotification.onclick = () => {
  window.focus();

  const type = sanitized.type?.toLowerCase() || "";

  if (type.includes("leave") && sanitized.leaveId) {
  window.dispatchEvent(
    new CustomEvent("openLeaveModal", {
      detail: { leaveId: sanitized.leaveId }
    })
  );
}
  else if (type.includes("interview") && sanitized.interviewId) {
    window.location.href = `/interviews?interviewId=${sanitized.interviewId}`;
  }
  else if (
    type.includes("birthday") ||
    type.includes("anniversary") ||
    type.includes("holiday") ||
    type.includes("announcement")
  ) {
    window.location.href = `/all-notifications`;
  }

  browserNotification.close();
};
}

    //  Prevent duplicates
    setNotifications(prev => {
      if (prev.some(n => n.id === sanitized.id)) return prev;
      return [sanitized, ...prev];
    });
  };

  socket.on("connect", onConnect);
  socket.on(`notify:${userId}`, handleNotification);

  //  If already connected, still trigger fetch
  if (socket.connected) {
    onConnect();
  }

  return () => {
    socket.off("connect", onConnect);
    socket.off(`notify:${userId}`, handleNotification);
    socket.disconnect();
  };
}, [userId, fetchUnreadNotifications]);
  return (
    <NotificationContext.Provider
      value={{
        notifications,
        fetchUnreadNotifications,
        fetchTodaysUnreadNotifications,
        markAllAsRead,
        loading,
        markAsRead,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};